import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSystem } from '@ohif/core';
import { usePolling } from './usePolling';
import { OrganEnum, OrganQualityEnum } from '../clients/viewer';
import { isSubmittable, OrganRow, OrganReviewService } from '../services/OrganReviewService';

const NO_ROWS: readonly OrganRow[] = Object.freeze([]);

/**
 * ONE polling owner, and one place that writes local state.
 *
 * develop had three effects (useOrganReview.ts:56-82): an initial fetch, a start/stop
 * driven by hasReviewInProgress, and an unmount cleanup that evicted the study. Its
 * timer also closed over the callback captured when it was armed, so changing series
 * mid-review polled the old seriesInstanceUID forever, and its setTimeout-then-
 * setInterval pair put the first tick at 2x the interval. M2's usePolling fixes all
 * three parts (usePolling.ts:3-16, defects 9a/9b/9c); nothing here re-implements them.
 */
export function useOrganReview(
  studyInstanceUid?: string,
  seriesInstanceUid?: string
): {
  rows: readonly OrganRow[];
  hasPending: boolean;
  isFullyReviewed: boolean;
  isSubmitDisabled: boolean;
  hasError: boolean;
  setQuality: (organ: OrganEnum, quality?: OrganQualityEnum) => void;
  submit: () => Promise<void>;
  refresh: () => Promise<void>;
} {
  const { servicesManager } = useSystem();
  const { organReviewService, uiNotificationService } = servicesManager.services;

  const [rows, setRows] = useState<readonly OrganRow[]>(() =>
    studyInstanceUid && seriesInstanceUid
      ? organReviewService.getRows(studyInstanceUid, seriesInstanceUid)
      : NO_ROWS
  );

  const [hasError, setHasError] = useState(false);

  const refresh = useCallback(
    () =>
      studyInstanceUid && seriesInstanceUid
        ? organReviewService.refresh(studyInstanceUid, seriesInstanceUid)
        : Promise.resolve(),
    [organReviewService, studyInstanceUid, seriesInstanceUid]
  );

  const { startPolling, stopPolling } = usePolling(refresh);

  useEffect(() => {
    // Both are undefined on the first renders: Mode.tsx:48 initialises
    // studyInstanceUIDs to null and sets it at :136, and the grid starts with
    // activeViewportId null (ViewportGridProvider.tsx:25-63).
    if (!studyInstanceUid || !seriesInstanceUid) {
      setRows(NO_ROWS);
      setHasError(false);
      return undefined;
    }

    const apply = () => {
      const next = organReviewService.getRows(studyInstanceUid, seriesInstanceUid);
      setRows(next);

      // Plan D-13: an errored series stops polling. Without this a row left inProgress
      // against a failing endpoint re-issues every 5 s for the life of the mode. The
      // retry surface is the button's own re-read on open (D-15) — this effect runs once
      // per series and cannot recover on its own.
      if (!organReviewService.hasError(seriesInstanceUid) &&
          next.some(row => row.state === 'inProgress')) {
        startPolling(refresh);
      } else {
        stopPolling();
      }

      setHasError(organReviewService.hasError(seriesInstanceUid));
    };

    const matches = (event: { studyInstanceUid: string; seriesInstanceUid: string }) =>
      event.studyInstanceUid === studyInstanceUid &&
      event.seriesInstanceUid === seriesInstanceUid;

    const subscriptions = [
      organReviewService.subscribe(
        OrganReviewService.EVENTS.ORGAN_REVIEWS_CHANGED,
        event => matches(event) && apply()
      ),

      // apply() cannot cover this: a 401 does not change the rows, so an inProgress
      // organ stays inProgress and the poller keeps re-issuing it. Same reason
      // useDiagnosisStatus.ts:85-99 subscribes to its own service's SESSION_LOST.
      organReviewService.subscribe(
        OrganReviewService.EVENTS.SESSION_LOST,
        event => matches(event) && stopPolling()
      ),
    ];

    apply();
    void refresh();

    return () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      stopPolling();
    };
  }, [organReviewService, studyInstanceUid, seriesInstanceUid, refresh, startPolling, stopPolling]);

  const setQuality = useCallback(
    (organ: OrganEnum, quality?: OrganQualityEnum) => {
      if (studyInstanceUid && seriesInstanceUid) {
        organReviewService.setQuality(studyInstanceUid, seriesInstanceUid, organ, quality);
      }
    },
    [organReviewService, studyInstanceUid, seriesInstanceUid]
  );

  /**
   * Plan D-6. develop raised both notifications from its hook (useOrganReview.ts:36-54)
   * and they are kept: a failed submit's only other signal is rows quietly reverting
   * from inProgress to gradeable — a negative, easy to miss, and indistinguishable at a
   * glance from nothing having happened.
   *
   * uiNotificationService is declared optional by platform/core's AppTypes.Services,
   * which the fork does not modify, so it is optional-chained rather than asserted —
   * unlike index.tsx:297's uiModalService, which is dereferenced twice and asserted once.
   */
  const submit = useCallback(async () => {
    if (!studyInstanceUid || !seriesInstanceUid) {
      return;
    }

    // Asked of the SERVICE, not of `rows`: submit() itself early-returns on an empty
    // submission, and `rows` is React state that can lag it by a tick — so testing the
    // hook's own copy would raise "Reviews submitted successfully" for a request that
    // was never issued.
    if (organReviewService.getSubmittable(studyInstanceUid, seriesInstanceUid).length === 0) {
      return;
    }

    try {
      await organReviewService.submit(studyInstanceUid, seriesInstanceUid);

      uiNotificationService?.show({
        title: 'Organ Review',
        message: 'Reviews submitted successfully',
        type: 'success',
        duration: 5000,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // `error` is deliberately unused and deliberately NAMED. An OPTIONAL catch
      // binding (`} catch {`, this tree's usual style, e.g. utils/cookies.ts:36)
      // crashes @babel/plugin-transform-regenerator when it appears inside an ASYNC
      // function: emit.ts:836 reads `.name` off the null param and throws "Cannot
      // read properties of null (reading 'name')", failing the whole test suite at
      // TRANSFORM time. That plugin is in the `test` env only (babel.config.js), so
      // dev and production builds are unaffected — which is exactly why this must not
      // be "tidied" back. Same fix as SessionService.ts's logOut() (:171-190); the
      // Task 4 plan flagged this exact catch as the site to apply it the day a spec
      // first imports this hook, which Task 7 is.
      uiNotificationService?.show({
        title: 'Organ Review',
        message: 'Failed to submit reviews',
        type: 'error',
        duration: 5000,
      });
    }
  }, [organReviewService, uiNotificationService, studyInstanceUid, seriesInstanceUid]);

  return useMemo(() => {
    const hasPending = rows.some(row => row.state === 'inProgress');

    return {
      rows,
      hasPending,
      hasError,
      isFullyReviewed: rows.length > 0 && rows.every(row => row.state === 'complete'),

      // Derived from `rows`, not read off the service during render, so the two cannot
      // disagree within one render — the tear useReportAssessment.ts:87-101 documents.
      isSubmitDisabled: hasPending || !rows.some(isSubmittable),

      setQuality,
      submit,
      refresh,
    };
  }, [rows, hasError, setQuality, submit, refresh]);
}
